"use client";

import { useSignUp } from "@clerk/nextjs";
import { useCallback, useRef, useState } from "react";
import { saveMarketingConsent } from "@/features/auth/api/save-marketing-consent";
import {
  hasClerkErrorCode,
  isClerkApiResponseError,
} from "@/features/auth/lib/clerk-errors";
import { useResendCooldown } from "./use-resend-cooldown";
import { useSessionNavigate } from "./use-session-navigate";

/** Clerk returns this code when the e-mail is already taken. */
const IDENTIFIER_EXISTS = "form_identifier_exists";

export type RegistrationInput = {
  password: string;
  /** Optional consent, persisted after the account exists. */
  marketing: boolean;
};

type UseSignUpActionsParams = {
  email: string;
  redirectUrl: string | null;
  onSignedIn?: () => void;
  setIsSubmitting: (value: boolean) => void;
  setGlobalError: (key: string | null) => void;
  /** The verification code has been sent — move on to the code step. */
  onCodeSent: () => void;
  /** The e-mail turned out to be taken after all, so sign in instead. */
  onAccountExists: () => void;
};

export const useSignUpActions = ({
  email,
  redirectUrl,
  onSignedIn,
  setIsSubmitting,
  setGlobalError,
  onCodeSent,
  onAccountExists,
}: UseSignUpActionsParams) => {
  const { signUp } = useSignUp();
  const navigate = useSessionNavigate({ redirectUrl, onSignedIn });
  const cooldown = useResendCooldown();

  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  // Collected on the password step, needed only after the session exists.
  const marketingConsentRef = useRef(false);

  const submitRegistration = useCallback(
    async ({ password, marketing }: RegistrationInput) => {
      setIsSubmitting(true);
      setNewPasswordError(null);
      setGlobalError(null);
      marketingConsentRef.current = marketing;

      try {
        const { error } = await signUp.password({
          emailAddress: email,
          password,
        });

        if (error) {
          if (hasClerkErrorCode(error, IDENTIFIER_EXISTS)) {
            onAccountExists();
            return;
          }

          // Clerk enforces its own policy (length, breached passwords). The
          // design has a single message for it, next to the rule list.
          if (isClerkApiResponseError(error)) {
            setNewPasswordError("auth.createPassword.validators.notMet");
          } else {
            setGlobalError("auth.genericError");
          }
          return;
        }

        const { error: sendError } = await signUp.verifications.sendEmailCode();

        if (sendError) {
          setGlobalError("auth.genericError");
          return;
        }

        cooldown.start();
        setCodeError(null);
        onCodeSent();
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      cooldown,
      email,
      onAccountExists,
      onCodeSent,
      setGlobalError,
      setIsSubmitting,
      signUp,
    ],
  );

  const submitVerificationCode = useCallback(
    async (code: string) => {
      setIsSubmitting(true);
      setCodeError(null);
      setGlobalError(null);

      try {
        const { error } = await signUp.verifications.verifyEmailCode({ code });

        if (error) {
          if (isClerkApiResponseError(error)) {
            setCodeError("auth.verifyCode.validators.invalid");
          } else {
            setGlobalError("auth.genericError");
          }
          return;
        }

        if (signUp.status !== "complete") {
          setGlobalError("auth.genericError");
          return;
        }

        await signUp.finalize({
          navigate: async (args) => {
            if (marketingConsentRef.current) {
              await saveMarketingConsent(true);
            }

            navigate(args);
          },
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [navigate, setGlobalError, setIsSubmitting, signUp],
  );

  const resendVerificationCode = useCallback(async () => {
    if (cooldown.isCoolingDown) {
      return;
    }

    setCodeError(null);
    setGlobalError(null);

    const { error } = await signUp.verifications.sendEmailCode();

    if (error) {
      setGlobalError("auth.genericError");
      return;
    }

    cooldown.start();
  }, [cooldown, setGlobalError, signUp]);

  const resetSignUp = useCallback(async () => {
    setNewPasswordError(null);
    setCodeError(null);
    await signUp.reset();
  }, [signUp]);

  return {
    codeError,
    newPasswordError,
    resendSecondsLeft: cooldown.secondsLeft,
    resendVerificationCode,
    resetSignUp,
    submitRegistration,
    submitVerificationCode,
  };
};
